// components/ServiceSlotsPage.tsx
import React, { useState, useEffect, useCallback } from 'react';

// Define the structure for the fetched data
interface RawDataFrame {
  [columnName: string]: {
    [rowIndex: string]: string | number; // Values can be string or number, like "✅", "❌", "Service not available"
  };
}

interface RowData {
  [key: string]: string | number;
}

const ServiceSlotsPage: React.FC = () => {
  const [data, setData] = useState<RowData[] | null>(null);
  const [isLoading, setIsLoading] = useState(false); // Set to false initially, fetch will set it true
  const [error, setError] = useState<string | null>(null);
  // This state will act as our refresh trigger.
  // Changing its value (e.g., to current timestamp) will re-run the useEffect.
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0); // Initialize with 0 to trigger initial fetch

  // Memoize the fetch function using useCallback to prevent unnecessary re-creations
  const fetchSlots = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setData(null); // Clear previous data while loading new

    try {
      const response = await fetch('https://interim-cabdemo-module.ispgnet.com/fetch_slots/');
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const rawData: RawDataFrame = await response.json();

      const transformedData: RowData[] = [];
      const columnNames = Object.keys(rawData);

      if (columnNames.length > 0) {
        const firstColumnData = rawData[columnNames[0]];
        // Convert string keys to numbers and sort them to maintain original order
        const rowIndices = Object.keys(firstColumnData).map(Number).sort((a, b) => a - b);

        rowIndices.forEach(index => {
          const row: RowData = {};
          columnNames.forEach(colName => {
            row[colName] = rawData[colName][String(index)];
          });
          transformedData.push(row);
        });
      }
      
      setData(transformedData);
    } catch (err) {
      console.error("Failed to fetch service slots:", err);
      setError(err instanceof Error ? err.message : "An unknown error occurred while fetching data.");
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array means this function is created once

  // useEffect now depends on refreshTrigger
  useEffect(() => {
    // Only fetch if refreshTrigger is explicitly set (or on initial mount due to initial value of 0)
    fetchSlots();
  }, [refreshTrigger, fetchSlots]); // fetchSlots is a dependency because it's defined outside useEffect

  const handleRefreshClick = () => {
    setRefreshTrigger(Date.now()); // Update trigger to current timestamp, forcing useEffect to run
  };

  const columnHeaders = data && data.length > 0 ? Object.keys(data[0]) : [];

  return (
    <>
      {/* Header Section - Similar to AlMajdouieCallApp */}
      <div className="flex items-center gap-3 bg-white p-4 border-b border-gray-200 shadow-sm">
        <h1 className="text-xl font-bold">🛠️ Service Slots Availability</h1>
      </div>

      {/* Main Content Area */}
      <div className="min-h-[calc(100vh-70px)] flex flex-col items-center pt-10 pb-6 bg-gray-100 px-4">
        <div className="max-w-7xl w-full mx-auto">
          <div className="p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">Service Centers & Slots</h2>
            
            {/* Refresh Button */}
            <div className="mb-6 text-right">
              <button
                onClick={handleRefreshClick}
                disabled={isLoading}
                className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ml-auto"
              >
                {isLoading ? (
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 01-1.896.635A5 5 0 0010 6V4a1 1 0 00-1-1H4zm7 14a1 1 0 00-1-1v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 011.896-.635A5 5 0 0010 14V16a1 1 0 001 1h5a1 1 0 000-2h-4z" clipRule="evenodd" />
                  </svg>
                )}
                {isLoading ? 'Refreshing...' : 'Refresh Data'}
              </button>
            </div>

            {isLoading && !data && ( // Only show full loading state if no data is present yet
              <div className="text-center py-8">
                <p className="text-lg text-blue-600">Loading service slot data...</p>
                <div className="mt-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" role="status">
                  <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">Loading...</span>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-100 text-red-800 p-4 rounded-lg text-center">
                <p className="font-semibold mb-2">Error fetching data:</p>
                <p>{error}</p>
              </div>
            )}

            {!isLoading && !error && data && data.length === 0 && (
              <div className="text-center py-8 text-gray-600">
                <p>No service slot data available.</p>
              </div>
            )}

            {!error && data && data.length > 0 && ( // Show data even if refreshing
              <div className="overflow-x-auto"> {/* Added for horizontal scrolling on small screens */}
                <table className="min-w-full table-auto border-collapse text-left">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {columnHeaders.map((header) => (
                        <th key={header} className="px-6 py-3 text-sm font-medium text-gray-600 uppercase tracking-wider">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.map((row, rowIndex) => (
                      <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        {columnHeaders.map((header) => (
                          <td key={`${rowIndex}-${header}`} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {/* Special rendering for ✅ and ❌ characters */}
                            {row[header] === '✅' ? (
                                <span className="text-green-600 font-bold">✅ Available</span>
                            ) : row[header] === '❌' ? (
                                <span className="text-red-600 font-bold">❌ Not Available</span>
                            ) : (
                                row[header]
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceSlotsPage;